import { api, extractListItems, extractPaging, extractResponsePaging, normalizePaging, type ApiResponse } from '../client';

// ================== TYPE DEFINITIONS ==================

// Donation Request Status Enum
export enum DonationRequestStatusEnum {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  COMPLETED = 'Completed',
  VERIFIED = 'Verified'
}

// Donation Request Status Interface
export interface DonationRequestStatus {
  uuid: string;
  name: string;
  description?: string;
}

// Charity Interface
export interface Charity {
  uuid: string;
  name: string;
  abn?: string;
  contact_email?: string;
  contact_phone?: string;
  head_office_location?: string;
  charity_government_id?: string;
  verified?: boolean;
  verified_bsb_no?: string;
  description?: string;
  logo?: string;
  website?: string;
  category?: Category;
}

// Employer Interface
export interface Employer {
  uuid: string;
  name: string;
  abn?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  head_office_location?: string;
  status?: string;
}

// User Interface
export interface User {
  uuid: string;
  email: string;
  email_verified_at?: string;
  full_name: string;
  phone?: string;
  credit_total?: number;
  job_title?: string;
  location?: string;
  department?: string;
  user_role?: {
    uuid: string;
    name: string;
  };
  user_status?: {
    uuid: string;
    name: string;
  };
}

// Donation Request Interface
export interface DonationRequest {
  uuid: string;
  charity_uuid: string;
  leave_hours: number;
  leave_value: number;
  created_at: string;
  updated_at: string;
  user_uuid: string;
  employer_uuid: string;
  approved_at: string;
  donation_request_status_uuid: string;
  payment_status?: 'not_paid' | 'paid' | 'completed';
  employer_payment_date?: string;
  charity_payment_date?: string;
  employer_payment_reference?: string;
  payout_uuid?: string;
  has_authorisation_pdf?: boolean;
  /** Canonical lifecycle stage from the backend (App\Support\DonationLifecycle) */
  lifecycle_stage?: string;
  rejection_reason?: string | null;
  rejected_at?: string | null;
  charity?: Charity;
  donation_request_status: DonationRequestStatus;
  user?: User;
  employer?: Employer;
}

// Pagination Interface
export interface PaginationInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  current_items: number;
  from: number;
  to: number;
  total: number;
}

// Filter Parameters Interface
export interface DonationRequestFilters {
  donation_request_status_uuid?: string;
  lifecycle_stage?: string;
  employer_uuid?: string;
  charity_uuid?: string;
  sort_by?: string;
  sort_direction?: string;
  user_uuid?: string;
  page?: number;
  per_page?: number;
  start_date?: string;
  end_date?: string;
}

// API Response Interfaces
export interface DonationRequestsResponse {
  success: boolean;
  message: string;
  data: DonationRequest[];
  paging: PaginationInfo;
}

export interface DonationRequestResponse {
  success: boolean;
  message: string;
  data: DonationRequest;
}

export interface DonationRequestStatusResponse {
  success: boolean;
  message: string;
  data: DonationRequestStatus[];
}

export interface ErrorResponse {
  success: false;
  message: string;
  data?: any;
}

// Create Donation Request Interface
export interface CreateDonationRequest {
  charity_uuid: string;
  leave_hours: number;
  employer_uuid: string;
  user_uuid: string;
  donation_request_status_uuid?: string;
}

// Charity Invitation Interface
export interface CharityInvitationRequest {
  name: string;
  contact_email: string;
  contact_phone?: string;
  website?: string;
  reason_for_request?: string;
  head_office_location?: string;
}

export interface CharityInvitationResponse {
  success: boolean;
  message: string;
  data?: {
    uuid: string;
    name: string;
    contact_email: string;
    created_at: string;
  };
}

// Charity Category Interface
export interface Category {
  uuid: string;
  name: string;
}

// Extended Charity Interface
export interface ExtendedCharity extends Charity {
  category?: Category;
  employerCharities?: Array<{
    uuid: string;
    employer_uuid: string;
    charity_uuid: string;
  }>;
  donationRequests?: Array<DonationRequest>;
}

// Charity List Response Interface
export interface CharityListResponse {
  success: boolean;
  message: string;
  data: ExtendedCharity[];
  paging?: PaginationInfo;
}

// Charity Response Interface
export interface CharityResponse {
  success: boolean;
  message: string;
  data: ExtendedCharity;
}

// Charity Filter Parameters Interface
export interface CharityFilters {
  phrase?: string;
  category_uuid?: string;
  verified?: boolean;
  include_my_invited?: boolean;
  sort_by?: string;
  sort_direction?: string;
  page?: number;
  per_page?: number;
}

// Update Charity Interface
export interface UpdateCharity {
  name?: string;
  abn?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
  head_office_location?: string;
  charity_government_id?: string;
  category_uuid?: string;
}

// ================== EMPLOYEE DONATION SERVICE ==================

export class EmployeeDonationService {

  /**
   * Get donation requests for a specific user with optional filters
   * 
   * @param userUuid User UUID to fetch donations for
   * @param filters Optional filter parameters
   * @returns Promise with donations list response
   */
  static async getDonationRequests(userUuid: string, filters?: Omit<DonationRequestFilters, 'user_uuid'>): Promise<{
    success: boolean;
    message: string;
    data?: DonationRequest[];
    paging?: PaginationInfo;
    errors?: any;
  }> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('user_uuid', userUuid);
      
      if (filters?.donation_request_status_uuid) {
        params.append('donation_request_status_uuid', filters.donation_request_status_uuid);
      }
      if (filters?.lifecycle_stage) {
        params.append('lifecycle_stage', filters.lifecycle_stage);
      }
      if (filters?.employer_uuid) {
        params.append('employer_uuid', filters.employer_uuid);
      }
      if (filters?.charity_uuid) {
        params.append('charity_uuid', filters.charity_uuid);
      }
      if (filters?.sort_by) {
        params.append('sort_by', filters.sort_by);
      }
      if (filters?.sort_direction) {
        params.append('sort_direction', filters.sort_direction);
      }
      if (filters?.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters?.end_date) {
        params.append('end_date', filters.end_date);
      }
      if (filters?.page) {
        params.append('page', filters.page.toString());
      }
      if (filters?.per_page) {
        params.append('per_page', filters.per_page.toString());
      }
      
      const queryString = params.toString();
      const url = `/donation-request/employee${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<DonationRequestsResponse>(url);
      
      if (response.data.success) {
        const items = extractListItems<DonationRequest>(response.data.data);
        const rawPaging = extractResponsePaging(response) ?? extractPaging(response.data.data);
        const paging = normalizePaging(rawPaging, items.length) as PaginationInfo;
        return {
          success: true,
          message: response.data.message,
          data: items,
          paging
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to load donation requests'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch donation requests',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Get a single donation request by UUID
   * 
   * @param uuid Donation request UUID
   * @returns Promise with donation request response
   */
  static async getDonationRequest(uuid: string): Promise<{
    success: boolean;
    message: string;
    data?: DonationRequest;
    errors?: any;
    statusCode?: number;
  }> {
    try {
      console.log('Fetching donation request:', uuid);
      const response = await api.get<DonationRequestResponse>(`/donation-request/${uuid}`);
      
      console.log('Donation request API response:', response);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        console.error('API returned success: false', response.data);
        return {
          success: false,
          message: response.data.message || 'Donation request not found'
        };
      }
    } catch (error: any) {
      console.error('Error fetching donation request:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        const status = error.response.status;
        if (status === 401) {
          return {
            success: false,
            message: 'Please log in to view this donation.',
            errors: errorData.data,
            statusCode: 401
          };
        }
        if (status === 403) {
          return {
            success: false,
            message: errorData.message || 'You don’t have permission to view this donation. It may belong to another account.',
            errors: errorData.data,
            statusCode: 403
          };
        }
        if (status === 404) {
          return {
            success: false,
            message: errorData.message || 'Donation not found.',
            errors: errorData.data,
            statusCode: 404
          };
        }
        return {
          success: false,
          message: errorData.message || `Failed to fetch donation request (${status})`,
          errors: errorData.data
        };
      } else if (error.response) {
        return {
          success: false,
          message: `Failed to fetch donation request: ${error.response.status} ${error.response.statusText}`,
          errors: { general: [`HTTP ${error.response.status}`] }
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch donation request (Network error)',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Download the Leave Cashout Authorisation PDF for an approved donation.
   * Uses the same endpoint as employer; policy allows the employee (owner) to access.
   *
   * @param uuid Donation request UUID
   * @param filename Optional filename for the download
   */
  static async downloadAuthorisationPdf(uuid: string, filename = 'Leave-Cashout-Authorisation.pdf'): Promise<void> {
    const response = await api.get<Blob>(`/donation-request/${uuid}/authorisation-pdf`, {
      responseType: 'blob',
    });
    const blob = response.data as unknown as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Create a new donation request
   * 
   * @param donationData Donation request data
   * @returns Promise with creation response
   */
  static async createDonationRequest(donationData: CreateDonationRequest): Promise<{
    success: boolean;
    message: string;
    data?: DonationRequest;
    errors?: any;
  }> {
    try {
      const response = await api.post<DonationRequestResponse>('/donation-request', donationData);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to create donation request'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to create donation request',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Get all available donation request statuses
   * 
   * @returns Promise with statuses response
   */
  static async getDonationRequestStatuses(): Promise<{
    success: boolean;
    message: string;
    data?: DonationRequestStatus[];
    errors?: any;
  }> {
    try {
      const response = await api.get<DonationRequestStatusResponse>('/donation-request-status');
      
      if (response.data.success) {
        // Ensure we always return an array
        const statuses = Array.isArray(response.data.data) ? response.data.data : [];
        return {
          success: true,
          message: response.data.message,
          data: statuses
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to load donation request statuses'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch donation request statuses',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Send an invitation to a charity organization
   * 
   * @param invitationData Charity invitation data
   * @returns Promise with invitation response
   */
  static async inviteCharity(invitationData: CharityInvitationRequest): Promise<{
    success: boolean;
    message: string;
    data?: {
      uuid: string;
      name: string;
      contact_email: string;
      created_at: string;
    };
    errors?: any;
  }> {
    try {
      const response = await api.post<CharityInvitationResponse>('/user-invitation/invite-charity', invitationData);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to send charity invitation'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to send charity invitation',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Get list of charities with optional filters
   * 
   * @param filters Optional filter parameters
   * @returns Promise with charities list response
   */
  static async getCharities(filters?: CharityFilters): Promise<{
    success: boolean;
    message: string;
    data?: ExtendedCharity[];
    paging?: PaginationInfo;
    errors?: any;
  }> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      if (filters?.phrase) {
        params.append('phrase', filters.phrase);
      }
      if (filters?.category_uuid) {
        params.append('category_uuid', filters.category_uuid);
      }
      if (filters?.verified !== undefined) {
        params.append('verified', filters.verified.toString());
      }
      if (filters?.include_my_invited !== undefined) {
        params.append('include_my_invited', filters.include_my_invited.toString());
      }
      if (filters?.sort_by) {
        params.append('sort_by', filters.sort_by);
      }
      if (filters?.sort_direction) {
        params.append('sort_direction', filters.sort_direction);
      }
      if (filters?.page) {
        params.append('page', filters.page.toString());
      }
      if (filters?.per_page) {
        params.append('per_page', filters.per_page.toString());
      }

      const queryString = params.toString();
      const url = `/charity${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<CharityListResponse>(url);
      
      if (response.data.success) {
        const items = extractListItems<ExtendedCharity>(response.data.data);
        const rawPaging = extractResponsePaging(response) ?? extractPaging(response.data.data);
        const paging = normalizePaging(rawPaging, items.length) as PaginationInfo;
        return {
          success: true,
          message: response.data.message,
          data: items,
          paging
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to load charities'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch charities',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Get a single charity by UUID
   * 
   * @param uuid Charity UUID
   * @returns Promise with charity response
   */
  static async getCharity(uuid: string): Promise<{
    success: boolean;
    message: string;
    data?: ExtendedCharity;
    errors?: any;
  }> {
    try {
      const response = await api.get<CharityResponse>(`/charity/${uuid}`);
      
      console.log('Charity API Response:', response);
      console.log('Charity API Response Data:', response.data);
      
      if (response.data.success) {
        // The API returns data directly in response.data.data
        const charityData = response.data.data;
        
        if (!charityData) {
          console.error('No charity data found in response:', response.data);
          return {
            success: false,
            message: 'Charity data not found in response'
          };
        }
        
        console.log('Extracted charity data:', charityData);
        
        return {
          success: true,
          message: response.data.message || 'Charity retrieved successfully',
          data: charityData
        };
      } else {
        console.error('API returned success: false', response.data);
        return {
          success: false,
          message: response.data.message || 'Charity not found'
        };
      }
    } catch (error: any) {
      console.error('Error fetching charity:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message || `Failed to fetch charity (${error.response.status})`,
          errors: errorData.data
        };
      } else if (error.response) {
        return {
          success: false,
          message: `Failed to fetch charity: ${error.response.status} ${error.response.statusText}`,
          errors: { general: [`HTTP ${error.response.status}`] }
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch charity (Network error)',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Update a charity
   * 
   * @param uuid Charity UUID
   * @param data Update data
   * @returns Promise with updated charity response
   */
  static async updateCharity(uuid: string, data: UpdateCharity): Promise<{
    success: boolean;
    message: string;
    data?: ExtendedCharity;
    errors?: any;
  }> {
    try {
      const response = await api.put<CharityResponse>(`/charity/${uuid}`, data);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to update charity'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to update charity',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Download donation receipt
   * 
   * @param uuid Donation request UUID
   * @returns Promise with blob data for the receipt
   */
  static async downloadReceipt(uuid: string): Promise<{
    success: boolean;
    message: string;
    data?: Blob;
    errors?: any;
  }> {
    try {
      const response = await api.get(`/donation-request/${uuid}/receipt`, {
        responseType: 'blob'
      });
      
      // Check if response.data is actually a Blob
      if (response.data instanceof Blob) {
        return {
          success: true,
          message: 'Receipt downloaded successfully',
          data: response.data
        };
      }
      
      return {
        success: false,
        message: 'Invalid receipt data received',
        errors: { general: ['Receipt data is not in the correct format'] }
      };
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to download receipt',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Generate a printable receipt for the donation
   * 
   * @param donation The donation request details
   * @returns HTML string for the receipt
   */
  static generateReceiptHTML(donation: DonationRequest): string {
    const date = new Date(donation.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const donationDays = Math.ceil(donation.leave_hours / 8);

    return `
      <html>
        <head>
          <title>Donation Receipt</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              padding-bottom: 20px;
              border-bottom: 2px solid #eee;
            }
            .logo {
              font-size: 24px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 10px;
            }
            .receipt-title {
              font-size: 28px;
              color: #1f2937;
              margin: 20px 0;
            }
            .details {
              margin: 30px 0;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              margin: 15px 0;
              padding: 10px 0;
              border-bottom: 1px solid #eee;
            }
            .label {
              font-weight: bold;
              color: #6b7280;
            }
            .value {
              color: #1f2937;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              color: #6b7280;
              font-size: 14px;
            }
            @media print {
              body {
                padding: 0;
                margin: 40px;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">LEAVE2GIVE</div>
            <div>Donation Receipt</div>
          </div>

          <h1 class="receipt-title">Donation Confirmation</h1>

          <div class="details">
            <div class="detail-row">
              <span class="label">Date:</span>
              <span class="value">${date}</span>
            </div>
            <div class="detail-row">
              <span class="label">Donation ID:</span>
              <span class="value">${donation.uuid}</span>
            </div>
            <div class="detail-row">
              <span class="label">Charity:</span>
              <span class="value">${donation.charity?.name || 'N/A'}</span>
            </div>
            <div class="detail-row">
              <span class="label">Donor:</span>
              <span class="value">${donation.user?.full_name || 'N/A'}</span>
            </div>
            <div class="detail-row">
              <span class="label">Leave Hours:</span>
              <span class="value">${donation.leave_hours} hours (${donationDays} days)</span>
            </div>
            <div class="detail-row">
              <span class="label">Status:</span>
              <span class="value">${donation.donation_request_status.name}</span>
            </div>
          </div>

          <div class="footer">
            <p>Thank you for your generous donation!</p>
            <p>This receipt was generated on ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `;
  }

  /**
   * Open the receipt in a new window for printing
   * 
   * @param donation The donation request details
   */
  static openReceipt(donation: DonationRequest): void {
    const receiptHTML = this.generateReceiptHTML(donation);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(receiptHTML);
      printWindow.document.close();
      // Wait for resources to load
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  }

  /**
   * Helper method to format currency
   * 
   * @param amount Amount in cents
   * @returns Formatted currency string
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  }

  /**
   * Helper method to format date
   * 
   * @param dateString ISO date string
   * @returns Formatted date string
   */
  static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Helper method to get status color
   * 
   * @param status Status name
   * @returns CSS color class
   */
  static getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  /**
   * Helper method to get status text
   * 
   * @param status Status name
   * @returns Formatted status text
   */
  static getStatusText(status: string): string {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  /**
   * Update a donation request (only charity can be changed for pending requests)
   * 
   * @param uuid Donation request UUID
   * @param data Update data (charity_uuid)
   * @returns Promise with updated donation request response
   */
  static async updateDonationRequest(uuid: string, data: { charity_uuid: string }): Promise<{
    success: boolean;
    message: string;
    data?: DonationRequest;
    errors?: any;
  }> {
    try {
      const response = await api.put<DonationRequestResponse>(`/donation-request/${uuid}`, data);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Donation request updated successfully',
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to update donation request'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message || 'Failed to update donation request',
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to update donation request',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Cancel (delete) a pending donation request
   * 
   * @param uuid Donation request UUID
   * @returns Promise with deletion response
   */
  static async cancelDonationRequest(uuid: string): Promise<{
    success: boolean;
    message: string;
    errors?: any;
  }> {
    try {
      const response = await api.delete<{ success: boolean; message: string }>(`/donation-request/${uuid}`);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Donation request cancelled successfully'
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to cancel donation request'
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message || 'Failed to cancel donation request',
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to cancel donation request',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Get employer details with linked donations for an employee
   * 
   * @param employerUuid Employer UUID
   * @param userUuid User UUID (current employee)
   * @returns Promise with employer details and donations
   */
  static async getEmployerWithDonations(employerUuid: string, userUuid: string): Promise<{
    success: boolean;
    message: string;
    data?: {
      employer: Employer;
      donations: DonationRequest[];
      paging?: PaginationInfo;
    };
    errors?: any;
  }> {
    try {
      // Fetch employer details
      const employerResponse = await api.get<ApiResponse<{ data: Employer }>>(`/employer/${employerUuid}`);
      
      if (!employerResponse.data.success) {
        return {
          success: false,
          message: employerResponse.data.message || 'Employer not found'
        };
      }

      // Extract employer data from response (response.data.data contains the employer)
      const employerData = (employerResponse.data as any).data;

      // Fetch linked donations for this employer and user
      const donationsResult = await this.getDonationRequests(userUuid, {
        employer_uuid: employerUuid,
        sort_by: 'created_at',
        sort_direction: 'desc',
        per_page: 100 // Get all donations for this employer
      });

      return {
        success: true,
        message: 'Employer details retrieved successfully',
        data: {
          employer: employerData,
          donations: donationsResult.data || [],
          paging: donationsResult.paging
        }
      };
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch employer details',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Get charity details with linked donations for an employee
   * 
   * @param charityUuid Charity UUID
   * @param userUuid User UUID (current employee)
   * @returns Promise with charity details and donations
   */
  static async getCharityWithDonations(charityUuid: string, userUuid: string): Promise<{
    success: boolean;
    message: string;
    data?: {
      charity: ExtendedCharity;
      donations: DonationRequest[];
      paging?: PaginationInfo;
    };
    errors?: any;
  }> {
    try {
      // Fetch charity details
      const charityResult = await this.getCharity(charityUuid);
      
      if (!charityResult.success || !charityResult.data) {
        return {
          success: false,
          message: charityResult.message || 'Charity not found'
        };
      }

      // Fetch linked donations for this charity and user
      // The employee endpoint filters by user_uuid, so we get donations from this user to this charity
      const donationsResult = await this.getDonationRequests(userUuid, {
        charity_uuid: charityUuid,
        sort_by: 'created_at',
        sort_direction: 'desc',
        per_page: 100 // Get all donations for this charity
      });

      console.log('Donations result for charity:', charityUuid, donationsResult);
      console.log('Donations data:', donationsResult.data);
      console.log('Donations count:', donationsResult.data?.length);

      return {
        success: true,
        message: 'Charity details retrieved successfully',
        data: {
          charity: charityResult.data,
          donations: donationsResult.data || [],
          paging: donationsResult.paging
        }
      };
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch charity details',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }
}

export default EmployeeDonationService; 